import React, {ReactNode} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../document/PortalDocument";
import MimeTypes from "../../document/mimetype/MimeTypes";
import {Button} from "primereact/button";
import SidebarRightMode from "../settings/SidebarRightMode";
import DocumentSettings from "../../document/DocumentSettings";

import {Annotation, AnnotationsDocument} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalContext from "../../PortalContext";
import Immutable from "../../../shared/immutable/Immutable";
import Translate from "../../../shared/translation/Translate";
import MouseFunction from "../settings/MouseFunction";
import {SidebarFunctionGlobals} from "../../document/components/pdf/SidebarFunctions";
import {AnnotationSubType} from "../pdf/annotation/AnnotationSubType";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {ScreenSize} from "../../../shared/models/ScreenSize";
import SidebarLeftMode from "../settings/SidebarLeftMode";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

export interface RightSidebarButtonsProperties extends SidebarFunctionGlobals, I18nGlobals {
	context: PortalContext;
	portalDocument?: Immutable<PortalDocumentInterface>;
}

export class RightSidebarButtonsDefinition extends PortalPureComponent<RightSidebarButtonsProperties, any> {
	private readonly hiddenAnnotations: Array<AnnotationSubType> = [
		AnnotationSubType.WIDGET,
		AnnotationSubType.LINK
	];

	constructor(props: Readonly<RightSidebarButtonsProperties>) {
		super(props);

		this.changeSidebarMode = this.changeSidebarMode.bind(this);
	}

	/**
	 * Change the sidebar right mode to global settings
	 */
	private changeSidebarMode(mode: SidebarRightMode): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		documentSettings.sidebarRightMode = documentSettings.sidebarRightMode === mode ? SidebarRightMode.NONE : mode;

		if (window.document.documentElement.offsetWidth < ScreenSize.SM) {
			documentSettings.sidebarLeftMode = SidebarLeftMode.NONE;
		}

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let isPortfolioFile: boolean = portalDocument.isPortfolioFile();
		if (portalDocument.isPortfolio() && isPortfolioFile) {
			return null;
		}

		let isPdf: boolean = portalDocument.getMimeType() === MimeTypes.PDF;
		let historySize: number = portalDocument.getHistorySize();
		let annotations: Array<Annotation> = FunctionHelper.applyValue(
			portalDocument.metadata?.annotations?.annotation, AnnotationsDocument.getAnnotationDefault()
		);
		let annotationCount: number = 0;

		for (let annotation of annotations) {
			if (!Object.values(AnnotationSubType).map((value) => value.toLowerCase()).includes(
				FunctionHelper.applyValue(annotation.subType?.toLowerCase(), "")
			) || this.hiddenAnnotations.map((value) => value.toLowerCase()).includes(
				FunctionHelper.applyValue(annotation.subType?.toLowerCase(), "")
			)) {
				// ignore all unknown or hidden annotations
				continue;
			}

			let parentNode: string | undefined = annotation.markup?.relation?.state?.objectID ||
				annotation.markup?.relation?.mark?.objectID || annotation.markup?.relation?.comment?.objectID;

			if (FunctionHelper.exists(parentNode)) {
				continue;
			}

			annotationCount++;
		}

		let securityIcon: Array<string> = ["icon", "icon-locked"];
		if (isPdf && portalDocument.hasPasswordProtection()) {
			securityIcon.push("password");
		}

		if (isPdf && portalDocument.hasCertificateProtection()) {
			securityIcon.push("certificate");
		}

		let mouseFunctions: Array<boolean> = [
			FunctionHelper.applyValue(this.props.showRedactAreaFunction, false),
			FunctionHelper.applyValue(this.props.showRedactTextFunction, false),
			FunctionHelper.applyValue(this.props.showAnnotationLineFunction, false),
			FunctionHelper.applyValue(this.props.showAnnotationCircleFunction, false),
			FunctionHelper.applyValue(this.props.showAnnotationSquareFunction, false),
			FunctionHelper.applyValue(this.props.showAnnotationMarkFunction, false),
			FunctionHelper.applyValue(this.props.showAnnotationUnderlineFunction, false),
			FunctionHelper.applyValue(this.props.showAnnotationSquigglyFunction, false),
			FunctionHelper.applyValue(this.props.showAnnotationStrikeFunction, false),
			FunctionHelper.applyValue(this.props.showAnnotationNoteFunction, false),
			FunctionHelper.applyValue(this.props.showAnnotationStampFunction, false)
		];

		let iconClass: Array<string> = ["icon"];
		switch (documentSettings.activeViewer.mouseFunction) {
			case MouseFunction.REDACT_AREA:
				iconClass.push("icon-redact-area");
				iconClass.push("small");
				break;
			case MouseFunction.REDACT_TEXT:
				iconClass.push("icon-redact-text");
				iconClass.push("small");
				break;
			case MouseFunction.LINE:
				iconClass.push("icon-annotation-line");
				break;
			case MouseFunction.CIRCLE:
				iconClass.push("icon-annotation-circle");
				break;
			case MouseFunction.SQUARE:
				iconClass.push("icon-annotation-square");
				break;
			case MouseFunction.MARK:
				iconClass.push("icon-annotation-mark");
				break;
			case MouseFunction.UNDERLINE:
				iconClass.push("icon-annotation-underline");
				break;
			case MouseFunction.SQUIGGLY:
				iconClass.push("icon-annotation-squiggly");
				break;
			case MouseFunction.STRIKE:
				iconClass.push("icon-annotation-strike");
				break;
			case MouseFunction.NOTE:
				iconClass.push("icon-annotation-note");
				break;
			case MouseFunction.STAMP_APPROVED:
			case MouseFunction.STAMP_EXPERIMENTAL:
			case MouseFunction.STAMP_NOT_APPROVED:
			case MouseFunction.STAMP_AS_IS:
			case MouseFunction.STAMP_EXPIRED:
			case MouseFunction.STAMP_NOT_FOR_PUBLIC_RELEASE:
			case MouseFunction.STAMP_CONFIDENTIAL:
			case MouseFunction.STAMP_FINAL:
			case MouseFunction.STAMP_SOLD:
			case MouseFunction.STAMP_DEPARTMENTAL:
			case MouseFunction.STAMP_FOR_COMMENT:
			case MouseFunction.STAMP_TOP_SECRET:
			case MouseFunction.STAMP_DRAFT:
			case MouseFunction.STAMP_FOR_PUBLIC_RELEASE:
				iconClass.push("icon-annotation-stamp");
				break;
		}

		return (
			<>
				{mouseFunctions.includes(true) &&
                    <Button className={documentSettings.showSidebarFunction ? "active" : ""}
                            onClick={() => {
								if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
									return;
								}

								let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
									this.props.portalDocument!.get().id!
								);
								let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
								viewerSettings.showSidebarFunction = !viewerSettings.showSidebarFunction;

								this.props.context.documentManager.updateGlobalDocumentList();
							}} data-pr-position={"left"}
                            tooltip={Translate.value("viewerscreen.toolbars.function", {ns: "screen"})}
                            icon={iconClass.join(" ")}/>
				}
				<Button icon="icon icon-info" data-pr-position={"left"}
						className={[
							"document-menu-info",
							documentSettings.sidebarRightMode === SidebarRightMode.INFO ? "active" : ""
						].join(" ")}
						onClick={() => this.changeSidebarMode(SidebarRightMode.INFO)}
						tooltip={Translate.value("viewerscreen.toolbars.information", {ns: "screen"})}/>
				{isPdf &&
                    <Button icon={securityIcon.join(" ")} data-pr-position={"left"}
                            className={[
								"document-menu-security",
								documentSettings.sidebarRightMode === SidebarRightMode.SECURITY ? "active" : ""
							].join(" ")}
                            onClick={() => this.changeSidebarMode(SidebarRightMode.SECURITY)}
                            tooltip={Translate.value("viewerscreen.toolbars.security", {ns: "screen"})}/>
				}
				{portalDocument.isViewable() && isPdf && !portalDocument.isPortfolio() &&
                    <Button icon="icon icon-annotation-note" data-pr-position={"left"}
                            className={[
								"document-menu-annotation",
								documentSettings.sidebarRightMode === SidebarRightMode.ANNOTATION ? "active" : ""
							].join(" ")}
                            onClick={() => this.changeSidebarMode(SidebarRightMode.ANNOTATION)}
                            badge={annotationCount ? annotationCount.toString() : undefined}
                            tooltip={Translate.value("viewerscreen.toolbars.annotation", {ns: "screen"})}/>
				}
				<Button icon="icon icon-log" badge={historySize > 1 ? historySize.toString() : undefined}
						className={[
							"document-menu-history",
							documentSettings.sidebarRightMode === SidebarRightMode.HISTORY ? "active" : ""
						].join(" ")}
						onClick={() => this.changeSidebarMode(SidebarRightMode.HISTORY)} data-pr-position={"left"}
						tooltip={Translate.value("viewerscreen.toolbars.history", {ns: "screen"})}/>
			</>
		);
	}
}

const RightSidebarButtons = React.memo(RightSidebarButtonsDefinition,
	(
		prevProps: Readonly<RightSidebarButtonsProperties>,
		nextProps: Readonly<RightSidebarButtonsProperties>
	) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (nextProps.showAnnotationLineFunction !== prevProps.showAnnotationLineFunction) {
			return false;
		}

		if (nextProps.showAnnotationCircleFunction !== prevProps.showAnnotationCircleFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquareFunction !== prevProps.showAnnotationSquareFunction) {
			return false;
		}

		if (nextProps.showAnnotationMarkFunction !== prevProps.showAnnotationMarkFunction) {
			return false;
		}

		if (nextProps.showAnnotationUnderlineFunction !== prevProps.showAnnotationUnderlineFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquigglyFunction !== prevProps.showAnnotationSquigglyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStrikeFunction !== prevProps.showAnnotationStrikeFunction) {
			return false;
		}

		if (nextProps.showAnnotationNoteFunction !== prevProps.showAnnotationNoteFunction) {
			return false;
		}

		if (nextProps.showRedactAreaFunction !== prevProps.showRedactAreaFunction) {
			return false;
		}

		if (nextProps.showRedactTextFunction !== prevProps.showRedactTextFunction) {
			return false;
		}

		if (nextProps.showAnnotationReplyFunction !== prevProps.showAnnotationReplyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStampFunction !== prevProps.showAnnotationStampFunction) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.showFormsEditFunction !== prevProps.showFormsEditFunction) {
			return false;
		}

		return true;
	}
);

export default RightSidebarButtons;